// @ts-nocheck
// =============================================================================
//  KitchenControl — MEP Module
//  Props: { config, synced, onConfigSave, activeTab, onTabChange }
//  activeTab: 'lijst' | 'afdeling' | 'categorie' | 'nieuwitem' | 'bestelbon' | 'archief'
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import {
  ClipboardList, Building2, FolderPlus, PlusCircle,
  FileText, Archive, X, Plus, Trash2, ChevronDown,
  ChevronRight, Search, Package, Printer,
} from "lucide-react";

// ── Kleuren ───────────────────────────────────────────────────────────────────
const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  greenBd: "#BBF7D0",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
};

// ── Constanten ────────────────────────────────────────────────────────────────
const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const UNITS = ["kg", "g", "L", "ml", "stuks", "bos", "blik", "zak", "el", "tl", "snuf", "overig"];
const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;

// ── ISO week helpers ──────────────────────────────────────────────────────────
function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7) };
}
function targetDate(dayIdx, weekOffset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const t = new Date(now);
  t.setDate(t.getDate() + (dayIdx - cur + weekOffset * 7));
  return t;
}
function dayKey(dayName, weekOffset = 0) {
  const idx = DAYS.indexOf(dayName);
  if (idx < 0) return dayName;
  const { year, week } = isoWeekOf(targetDate(idx, weekOffset));
  return `${year}-W${String(week).padStart(2, "0")}-${dayName}`;
}
function getWeekNr(weekOffset = 0) {
  const now = new Date();
  const d = new Date(now);
  d.setDate(d.getDate() + weekOffset * 7);
  return isoWeekOf(d).week;
}
function getWeekLabel(weekOffset = 0) {
  const cur = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const mon = new Date(); mon.setDate(mon.getDate() - cur + weekOffset * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const M = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
  return `${mon.getDate()} ${M[mon.getMonth()]} – ${sun.getDate()} ${M[sun.getMonth()]}`;
}
function getDayDate(dayIdx, weekOffset = 0) {
  return targetDate(dayIdx, weekOffset).getDate();
}

// ── Lokale opslag ─────────────────────────────────────────────────────────────
const LS_KEY = "kc-mep-v1";
function loadLocal() {
  try {
    const s = localStorage.getItem(LS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}


// ── Supabase sync-laag ────────────────────────────────────────────────────────
// Props sb + tenantId worden meegegeven vanuit App.jsx (zelfde patroon als HACCP)
// Tabel: mep_data { id, tenant_id, data jsonb, updated_at }
// Realtime: luistert op UPDATE van eigen tenant-rij → merge binnenkomende data

async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[MEP] load failed:", e); }
  return null;
}

async function sbSave(sb, tenantId, data) {
  try {
    // Upsert: bestaat de rij al? Dan PATCH, anders POST
    const exists = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
    if (exists?.length) {
      await sb.update("mep_data", `tenant_id=eq.${tenantId}`, {
        data,
        updated_at: new Date().toISOString(),
      });
    } else {
      await sb.insert("mep_data", {
        tenant_id: tenantId,
        data,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (e) { console.warn("[MEP] save failed:", e); }
}

// Merge inkomende realtime state met lokale state
// Kritiek: checks worden per item+dag gemerged — niet overschreven
// Zo gaan aanvinkjes van device 2 niet verloren als device 1 iets anders aanpast
function mergeState(local, remote) {
  if (!remote) return local;
  // Items: remote wint voor structuur (naam, bestelartikelen etc.)
  // maar checks worden gemerged
  const localChecksById = {};
  (local.items || []).forEach(it => { localChecksById[it.id] = it.checks || {}; });

  const mergedItems = (remote.items || []).map(remoteIt => {
    const localChecks = localChecksById[remoteIt.id] || {};
    const remoteChecks = remoteIt.checks || {};
    // Merge: remote wint tenzij lokaal een recentere check heeft op dezelfde dag
    // Simpele strategie: union van beide, remote wint bij conflict
    return { ...remoteIt, checks: { ...localChecks, ...remoteChecks } };
  });

  return {
    ...remote,
    items: mergedItems,
  };
}

// ── Initiële staat ────────────────────────────────────────────────────────────
function initState() {
  const saved = loadLocal();
  if (saved?.afdelingen?.length) return saved;
  const afdId = uid();
  return {
    afdelingen: [{ id: afdId, naam: "Warme kant" }],
    categorieen: [
      { id: uid(), naam: "Vlees",            afdeling_id: afdId },
      { id: uid(), naam: "Vis & Zeevruchten", afdeling_id: afdId },
      { id: uid(), naam: "Soepen",            afdeling_id: afdId },
      { id: uid(), naam: "Overig",            afdeling_id: afdId },
    ],
    items: [],
  };
}

// ── Gedeelde stijlen ──────────────────────────────────────────────────────────
const inp = {
  background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt,
  padding: "7px 10px", fontFamily: "inherit", fontSize: 13,
  borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box",
};
const btn = (bg, fg, bd) => ({
  background: bg, color: fg, border: `1px solid ${bd || bg}`,
  padding: "7px 14px", fontFamily: "inherit", fontSize: 12,
  fontWeight: 600, borderRadius: 6, cursor: "pointer",
});

// ─────────────────────────────────────────────────────────────────────────────
// PRINT CSS
// ─────────────────────────────────────────────────────────────────────────────
function printBestelbon(byLev, weekLabel, all) {
  const win = window.open("", "_blank", "width=800,height=600");
  if (!win) return;

  const regelsHtml = Object.entries(byLev)
    .sort(([a], [b]) => a.localeCompare(b, "nl"))
    .map(([lev, regels]) => `
      <div class="leverancier">${lev} <span class="count">(${regels.length})</span></div>
      ${regels.map(r => `
        <div class="rij">
          <div class="naam">
            ${r.naam}
            ${r.bronnen.length > 0 ? `<span class="bron">via: ${r.bronnen.join(", ")}</span>` : ""}
          </div>
          <div class="hoev">${r.hoeveelheid > 0 ? `${r.hoeveelheid % 1 === 0 ? r.hoeveelheid : r.hoeveelheid.toFixed(2)} ${r.eenheid}` : ""}</div>
        </div>
      `).join("")}
    `).join("");

  win.document.write(`<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="utf-8">
  <title>Bestelbon – ${weekLabel}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, sans-serif; font-size: 12pt; color: #000; padding: 20mm 15mm; }
    h1 { font-size: 18pt; font-weight: 800; margin-bottom: 4px; }
    .meta { font-size: 10pt; color: #555; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #000; }
    .leverancier { font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; margin: 18px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #aaa; color: #333; }
    .count { font-weight: 400; font-size: 9pt; color: #888; }
    .rij { display: flex; justify-content: space-between; align-items: baseline; padding: 5px 0; border-bottom: 1px solid #eee; }
    .naam { flex: 1; font-weight: 500; }
    .bron { font-size: 9pt; color: #888; margin-left: 8px; font-weight: 400; }
    .hoev { font-weight: 700; font-size: 12pt; min-width: 80px; text-align: right; }
    @page { margin: 15mm; }
  </style>
</head>
<body>
  <h1>Bestelbon</h1>
  <div class="meta">${weekLabel} &nbsp;·&nbsp; ${all.length} artikel${all.length !== 1 ? "en" : ""}</div>
  ${regelsHtml}
  <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; }</script>
</body>
</html>`);
  win.document.close();
}


// ─────────────────────────────────────────────────────────────────────────────
// MEP MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function MepModule({ config, synced, onConfigSave, activeTab, onTabChange, sb, tenantId, onOpenRecept }) {
  const [state, setState] = useState(initState);
  const [weekOffset, setWeekOffset] = useState(0);
  // activeAfdId los initialiseren op basis van dezelfde localStorage-data
  const [activeAfdId, setActiveAfdId] = useState(() => {
    const s = loadLocal();
    if (s?.afdelingen?.length) return s.afdelingen[0].id;
    // Geen opgeslagen data: pak de id uit de verse state
    return null; // wordt gereset door useEffect hieronder
  });
  const [openCats, setOpenCats] = useState({});     // { catId: true } = uitgeklapt
  const [activeDag, setActiveDag] = useState(() => new Date().getDay() === 0 ? 6 : new Date().getDay() - 1); // 0=Ma..6=Zo
  const [itemPopup, setItemPopup] = useState(null); // null | { mode:'new'|'edit', item? }
  const [afdPopup, setAfdPopup] = useState(false);
  const [catPopup, setCatPopup] = useState(false);
  const [search, setSearch] = useState("");
  const [syncStatus, setSyncStatus] = useState("local"); // 'local' | 'syncing' | 'synced' | 'error'
  const realtimeRef = useRef(null);   // Supabase Realtime channel
  const saveTimerRef = useRef(null);  // debounce timer voor opslaan
  const localRef = useRef(false);     // voorkomt echo van eigen updates

  // Zijbalk-navigatie triggers
  useEffect(() => {
    if (activeTab === "nieuwitem")  { setItemPopup({ mode: "new" }); onTabChange?.("lijst"); }
    if (activeTab === "afdeling")   { setAfdPopup(true);             onTabChange?.("lijst"); }
    if (activeTab === "categorie")  { setCatPopup(true);             onTabChange?.("lijst"); }
  }, [activeTab]);

  const { afdelingen, categorieen, items } = state;
  const tab = activeTab || "lijst";

  const update = useCallback((fn) => setState(s => {
    const next = { ...s, ...fn(s) };
    saveLocal(next);
    // Debounced Supabase save: wacht 600ms na laatste wijziging
    if (sb && tenantId) {
      localRef.current = true;
      setSyncStatus("syncing");
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        await sbSave(sb, tenantId, next);
        setSyncStatus("synced");
        // Reset localRef na korte delay zodat Realtime echo genegeerd wordt
        setTimeout(() => { localRef.current = false; }, 500);
      }, 600);
    }
    return next;
  }), [sb, tenantId]);

  // ── Supabase: laden bij mount + Realtime abonnement ──────────────────────────
  useEffect(() => {
    if (!sb || !tenantId) return;

    // Initieel laden vanuit Supabase
    (async () => {
      setSyncStatus("syncing");
      const remote = await sbLoad(sb, tenantId);
      if (remote?.afdelingen?.length) {
        setState(local => {
          const merged = mergeState(local, remote);
          saveLocal(merged);
          return merged;
        });
      }
      setSyncStatus("synced");
    })();

    // Realtime: luister op wijzigingen in mep_data voor deze tenant
    // Gebruikt Supabase REST + polling als fallback (werkt zonder supabase-js client)
    let pollInterval = null;
    let lastUpdatedAt = null;

    const poll = async () => {
      if (localRef.current) return; // eigen update, negeer
      try {
        const rows = await sb.select(
          "mep_data",
          `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`
        );
        if (!rows?.length) return;
        const row = rows[0];
        if (lastUpdatedAt && row.updated_at === lastUpdatedAt) return; // geen wijziging
        if (!lastUpdatedAt) { lastUpdatedAt = row.updated_at; return; } // eerste poll, sla op
        lastUpdatedAt = row.updated_at;
        // Externe wijziging — merge met lokale state
        setState(local => {
          const merged = mergeState(local, row.data);
          saveLocal(merged);
          return merged;
        });
      } catch {}
    };

    // Poll elke 2 seconden — snel genoeg voor keukengebruik, zuinig op resources
    pollInterval = setInterval(poll, 2000);
    // Eerste poll direct
    poll();

    return () => {
      clearInterval(pollInterval);
      clearTimeout(saveTimerRef.current);
    };
  }, [sb, tenantId]);

  // ── Actieve afdeling instellen: null (eerste load) of verdwenen afdeling → eerste
  useEffect(() => {
    if (afdelingen.length && (!activeAfdId || !afdelingen.find(a => a.id === activeAfdId))) {
      setActiveAfdId(afdelingen[0].id);
    }
  }, [afdelingen, activeAfdId]);

  // Toggle check: leeg → gepland(amber) → gedaan(groen) → leeg
  const toggleCheck = (itemId, dk) => {
    update(s => ({
      items: s.items.map(it => {
        if (it.id !== itemId) return it;
        const cur = it.checks?.[dk] || 0;
        const next = (cur + 1) % 3;
        const checks = { ...it.checks, [dk]: next };
        if (next === 0) delete checks[dk];
        return { ...it, checks };
      })
    }));
  };

  // Bestelbon items berekenen
  const bestelbonItems = (() => {
    const map = {};
    items.filter(it => !it.archived).forEach(it => {
      const heeftPlan = DAYS.some((d, i) => {
        const dk = dayKey(d, weekOffset);
        return (it.checks?.[dk] || 0) > 0;
      });
      if (!heeftPlan) return;
      (it.bestelartikelen || []).forEach(ba => {
        if (!ba.naam?.trim()) return;
        const k = `${ba.naam.trim().toLowerCase()}|${ba.eenheid}`;
        if (!map[k]) map[k] = { naam: ba.naam.trim(), eenheid: ba.eenheid, hoeveelheid: 0, leverancier: ba.leverancier || "Overig", bronnen: [] };
        map[k].hoeveelheid += parseFloat(ba.hoeveelheid) || 0;
        if (!map[k].bronnen.includes(it.naam)) map[k].bronnen.push(it.naam);
      });
    });
    const all = Object.values(map).sort((a, b) => a.naam.localeCompare(b.naam, "nl"));
    const byLev = {};
    all.forEach(r => {
      const l = r.leverancier || "Overig";
      if (!byLev[l]) byLev[l] = [];
      byLev[l].push(r);
    });
    return { all, byLev };
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>
      {/* ── Header ── */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
          {tab === "bestelbon" ? "🧾 Bestelbon" : tab === "archief" ? "🗂 Archief" : "📋 MEP"}
        </span>
        <div style={{ flex: 1 }} />
        {/* Sync status */}
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : syncStatus === "error" ? "#FCA5A5" : "rgba(255,255,255,.45)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : syncStatus === "error" ? "#F87171" : "#4ADE80", flexShrink: 0 }} />
            {syncStatus === "syncing" ? "synchroniseren…" : syncStatus === "error" ? "sync fout" : "gesynchroniseerd"}
          </div>
        )}
        {tab === "bestelbon" && (
          <button
            onClick={() => printBestelbon(bestelbonItems.byLev, `Week ${getWeekNr(weekOffset)} · ${getWeekLabel(weekOffset)}`, bestelbonItems.all)}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600 }}
          >
            <Printer size={14} /> Afdrukken
          </button>
        )}
        {tab === "lijst" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: C.txt3, pointerEvents: "none" }} />
              <input
                style={{ ...inp, width: 140, padding: "5px 8px 5px 26px", fontSize: 12, background: C.hdrBdr, border: "none", color: "#fff", borderRadius: 6 }}
                placeholder="Zoeken…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}
      </header>

      {/* ── Week nav + Afdeling tabs + Dag selector (lijst-view) ── */}
      {tab === "lijst" && (
        <>
          {/* Week navigatie */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 16px", flexShrink: 0 }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{ background: "none", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>‹</button>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>Week {getWeekNr(weekOffset)}</div>
              <div style={{ fontSize: 11, color: C.txt3 }}>{getWeekLabel(weekOffset)}</div>
            </div>
            <button onClick={() => setWeekOffset(w => w + 1)} style={{ background: "none", border: `1px solid ${C.bdr}`, color: C.txt2, padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>›</button>
          </div>

          {/* Dag selector */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", gap: 6, padding: "10px 16px", flexShrink: 0, overflowX: "auto" }}>
            {DAYS.map((d, i) => {
              const isToday = i === (new Date().getDay() === 0 ? 6 : new Date().getDay() - 1) && weekOffset === 0;
              const isActief = activeDag === i;
              const datum = getDayDate(i, weekOffset);
              const dk = dayKey(d, weekOffset);
              // Tel hoeveel items aangevinkt zijn op deze dag
              const gepland = state.items.filter(it => !it.archived && it.afdeling_id === activeAfdId && (it.checks?.[dk] || 0) > 0).length;
              return (
                <button key={d} onClick={() => setActiveDag(i)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 52, background: isActief ? C.hdr : "transparent", color: isActief ? "#fff" : isToday ? C.pri : C.txt2, transition: "background .12s" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>{d}</span>
                  <span style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{datum}</span>
                  {gepland > 0 && <span style={{ width: 6, height: 6, borderRadius: "50%", background: isActief ? "#fff" : C.pri, marginTop: 3 }} />}
                  {gepland === 0 && <span style={{ width: 6, height: 6, marginTop: 3 }} />}
                </button>
              );
            })}
          </div>

          {/* Afdeling tabs */}
          <div style={{ background: C.bg2, borderBottom: `1px solid ${C.bdr}`, display: "flex", gap: 0, flexShrink: 0, overflowX: "auto" }}>
            {afdelingen.map(afd => (
              <button key={afd.id} onClick={() => setActiveAfdId(afd.id)}
                style={{ padding: "8px 18px", fontFamily: "inherit", fontSize: 13, fontWeight: activeAfdId === afd.id ? 700 : 400, cursor: "pointer", border: "none", borderBottom: activeAfdId === afd.id ? `2px solid ${C.pri}` : "2px solid transparent", background: "transparent", color: activeAfdId === afd.id ? C.pri : C.txt2, whiteSpace: "nowrap", transition: "all .12s" }}>
                {afd.naam}
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "lijst"     && <LijstView state={state} activeAfdId={activeAfdId} weekOffset={weekOffset} activeDag={activeDag} search={search} openCats={openCats} setOpenCats={setOpenCats} toggleCheck={toggleCheck} onEditItem={item => setItemPopup({ mode: "edit", item })} />}
        {tab === "bestelbon" && <BestelbonView bestelbonItems={bestelbonItems} weekOffset={weekOffset} />}
        {tab === "archief"   && <ArchiefView state={state} update={update} onEditItem={item => setItemPopup({ mode: "edit", item })} />}
      </div>

      {/* ── Popups ── */}
      {itemPopup && (
        <ItemPopup
          mode={itemPopup.mode}
          item={itemPopup.item}
          state={state}
          activeAfdId={activeAfdId}
          onSave={(item) => {
            update(s => ({
              items: itemPopup.mode === "new"
                ? [...s.items, item]
                : s.items.map(i => i.id === item.id ? item : i)
            }));
            setItemPopup(null);
          }}
          onDelete={(id) => { update(s => ({ items: s.items.filter(i => i.id !== id) })); setItemPopup(null); }}
          onArchive={(id) => { update(s => ({ items: s.items.map(i => i.id === id ? { ...i, archived: true } : i) })); setItemPopup(null); }}
          onClose={() => setItemPopup(null)}
          onOpenRecept={onOpenRecept}
        />
      )}
      {afdPopup && (
        <AfdPopup
          afdelingen={afdelingen}
          onSave={(afd) => { update(s => ({ afdelingen: [...s.afdelingen, afd] })); }}
          onRename={(id, naam) => { update(s => ({ afdelingen: s.afdelingen.map(a => a.id === id ? { ...a, naam } : a) })); }}
          onDelete={(id) => { update(s => ({ afdelingen: s.afdelingen.filter(a => a.id !== id), categorieen: s.categorieen.filter(c => c.afdeling_id !== id) })); }}
          onClose={() => setAfdPopup(false)}
        />
      )}
      {catPopup && (
        <CatPopup
          categorieen={categorieen}
          afdelingen={afdelingen}
          activeAfdId={activeAfdId}
          onSave={(cat) => { update(s => ({ categorieen: [...s.categorieen, cat] })); }}
          onRename={(id, naam) => { update(s => ({ categorieen: s.categorieen.map(c => c.id === id ? { ...c, naam } : c) })); }}
          onDelete={(id) => { update(s => ({ categorieen: s.categorieen.filter(c => c.id !== id), items: s.items.map(i => i.categorie_id === id ? { ...i, categorie_id: null } : i) })); }}
          onClose={() => setCatPopup(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LIJST VIEW — dag-weergave zoals BijHENC
// ─────────────────────────────────────────────────────────────────────────────
function LijstView({ state, activeAfdId, weekOffset, activeDag, search, openCats, setOpenCats, toggleCheck, onEditItem }) {
  const { categorieen, items } = state;
  const dk = dayKey(DAYS[activeDag], weekOffset);

  const cats = categorieen.filter(c => c.afdeling_id === activeAfdId);
  const zichtbareItems = items.filter(it =>
    !it.archived &&
    it.afdeling_id === activeAfdId &&
    (!search || it.naam.toLowerCase().includes(search.toLowerCase()))
  );
  const catIds = new Set(cats.map(c => c.id));

  // Teller: hoeveel items nog te doen vandaag (gepland maar niet gedaan)
  const teDoen = zichtbareItems.filter(it => (it.checks?.[dk] || 0) === 1).length;
  // Progressbalk: gedaan / gepland
  const geplandTotaal = zichtbareItems.filter(it => (it.checks?.[dk] || 0) > 0).length;
  const gedaanTotaal  = zichtbareItems.filter(it => (it.checks?.[dk] || 0) === 2).length;
  const progPct = geplandTotaal > 0 ? (gedaanTotaal / geplandTotaal) * 100 : 0;

  if (cats.length === 0 && zichtbareItems.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: C.txt3 }}>
        <ClipboardList size={40} strokeWidth={1} />
        <p style={{ fontSize: 14 }}>Nog geen items. Voeg een categorie en item toe via de zijbalk.</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Progressbalk */}
      <div style={{ height: 4, background: C.bg3, flexShrink: 0, position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${progPct}%`, background: C.amber, borderRadius: 2, transition: "width .3s" }} />
        {teDoen > 0 && (
          <div style={{ position: "absolute", right: 12, top: 6, fontSize: 11, color: C.amber, fontWeight: 700 }}>
            {teDoen} te doen
          </div>
        )}
      </div>

      {/* Lijst */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {cats.map(cat => {
          const catItems = zichtbareItems.filter(it => it.categorie_id === cat.id);
          if (catItems.length === 0 && search) return null;
          const open = openCats[cat.id] !== false;
          const catGedaan = catItems.filter(it => (it.checks?.[dk] || 0) === 2).length;
          const catGepland = catItems.filter(it => (it.checks?.[dk] || 0) > 0).length;

          return (
            <div key={cat.id}>
              {/* Categorie header */}
              <div onClick={() => setOpenCats(o => ({ ...o, [cat.id]: !open }))}
                style={{ display: "flex", alignItems: "center", padding: "8px 16px", background: C.bg3, borderBottom: `1px solid ${C.bdr}`, cursor: "pointer", userSelect: "none" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, flex: 1 }}>{cat.naam}</span>
                <span style={{ fontSize: 10, color: C.txt3, marginRight: 8 }}>{catGedaan}/{catItems.length}</span>
                {open ? <ChevronDown size={13} color={C.txt3} /> : <ChevronRight size={13} color={C.txt3} />}
              </div>
              {/* Items */}
              {open && catItems.map(it => (
                <ItemRijDag key={it.id} item={it} dk={dk} toggleCheck={toggleCheck} onEdit={() => onEditItem(it)} />
              ))}
            </div>
          );
        })}

        {/* Items zonder categorie */}
        {zichtbareItems.filter(it => !it.categorie_id || !catIds.has(it.categorie_id)).map(it => (
          <ItemRijDag key={it.id} item={it} dk={dk} toggleCheck={toggleCheck} onEdit={() => onEditItem(it)} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEM RIJ — dag-weergave
// ─────────────────────────────────────────────────────────────────────────────
function ItemRijDag({ item, dk, toggleCheck, onEdit }) {
  const st = item.checks?.[dk] || 0;
  const gedaan = st === 2;
  const gepland = st === 1;

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2, gap: 12 }}>
      {/* Checkbox */}
      <button onClick={() => toggleCheck(item.id, dk)}
        style={{
          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
          border: `2px solid ${st === 0 ? C.bdr2 : gepland ? C.amber : C.green}`,
          background: st === 0 ? "transparent" : gepland ? C.amberBg : C.greenBg,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, fontWeight: 800, color: gepland ? C.amber : C.green,
          transition: "all .1s", padding: 0,
        }}>
        {st === 0 ? "" : gepland ? "×" : "✓"}
      </button>

      {/* Naam + badges */}
      <div style={{ flex: 1, cursor: "pointer", minWidth: 0 }} onClick={onEdit}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 13, fontWeight: 500,
            color: gedaan ? C.txt3 : C.txt,
            textDecoration: gedaan ? "line-through" : "none",
          }}>{item.naam}</span>
          {item.recept_id && (
            <span style={{ fontSize: 10, background: C.priBg, color: C.pri, border: `1px solid ${C.priBd}`, padding: "1px 7px", borderRadius: 10, fontWeight: 600 }}>
              RECEPT
            </span>
          )}
          {item.bestelartikelen?.length > 0 && (
            <span style={{ fontSize: 10, color: C.txt3, display: "flex", alignItems: "center", gap: 2 }}>
              <Package size={9} />{item.bestelartikelen.length}
            </span>
          )}
        </div>
        {item.notitie && !gedaan && (
          <div style={{ fontSize: 11, color: C.txt3, marginTop: 2, fontStyle: "italic" }}>{item.notitie}</div>
        )}
      </div>

      {/* Doorstuur-pijl */}
      <ChevronRight size={14} color={C.txt3} style={{ flexShrink: 0, cursor: "pointer" }} onClick={onEdit} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BESTELBON VIEW
// ─────────────────────────────────────────────────────────────────────────────
function BestelbonView({ bestelbonItems, weekOffset }) {
  const { all, byLev } = bestelbonItems;
  const weekLabel = `Week ${getWeekNr(weekOffset)} · ${getWeekLabel(weekOffset)}`;

  if (all.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: C.txt3 }}>
        <FileText size={40} strokeWidth={1} />
        <p style={{ fontSize: 14 }}>Geen items gepland deze week.</p>
        <p style={{ fontSize: 12 }}>Vink items aan in de weekplanning om ze op de bestelbon te zetten.</p>
      </div>
    );
  }

  return (
    <div className="kc-mep-bestelbon" style={{ flex: 1, overflowY: "auto", padding: 20 }}>
      {/* Print header — alleen zichtbaar op print */}
      <h1 style={{ display: "none" }} className="print-only">Bestelbon</h1>
      <div className="print-meta" style={{ display: "none" }}>
        {weekLabel} · {all.length} artikel{all.length !== 1 ? "en" : ""}
      </div>

      {/* Scherm meta */}
      <div className="print-hide" style={{ marginBottom: 16, fontSize: 12, color: C.txt2 }}>
        {weekLabel} · {all.length} artikel{all.length !== 1 ? "en" : ""}
      </div>

      {Object.entries(byLev).sort(([a], [b]) => a.localeCompare(b, "nl")).map(([lev, regels]) => (
        <div key={lev} style={{ marginBottom: 20 }}>
          <div className="print-leverancier" style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <span className="print-hide"><Package size={12} /></span>
            {lev}
            <span className="print-hide" style={{ fontSize: 10, fontWeight: 400, color: C.txt3 }}>({regels.length})</span>
          </div>
          {regels.map((r, i) => (
            <div key={i} className="print-rij" style={{ display: "flex", alignItems: "center", padding: "9px 14px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
              <div style={{ flex: 1 }}>
                <span className="print-rij-naam" style={{ fontWeight: 600, color: C.txt }}>{r.naam}</span>
                {r.bronnen.length > 0 && (
                  <span className="print-rij-bron" style={{ fontSize: 11, color: C.txt3, marginLeft: 8 }}>
                    via: {r.bronnen.join(", ")}
                  </span>
                )}
              </div>
              {r.hoeveelheid > 0 && (
                <span className="print-rij-hoeveelheid" style={{ fontSize: 13, fontWeight: 700, color: C.pri }}>
                  {r.hoeveelheid % 1 === 0 ? r.hoeveelheid : r.hoeveelheid.toFixed(2)} {r.eenheid}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ARCHIEF VIEW
// ─────────────────────────────────────────────────────────────────────────────
function ArchiefView({ state, update, onEditItem }) {
  const gearchiveerd = state.items.filter(i => i.archived);

  const herstel = (id) => update(s => ({ items: s.items.map(i => i.id === id ? { ...i, archived: false } : i) }));
  const verwijder = (id) => { if (confirm("Item definitief verwijderen?")) update(s => ({ items: s.items.filter(i => i.id !== id) })); };

  if (gearchiveerd.length === 0) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: C.txt3 }}>
        <Archive size={40} strokeWidth={1} />
        <p style={{ fontSize: 14 }}>Geen gearchiveerde items.</p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
        Archief ({gearchiveerd.length})
      </div>
      {gearchiveerd.map(item => (
        <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 6, marginBottom: 4 }}>
          <div style={{ flex: 1, cursor: "pointer" }} onClick={() => onEditItem(item)}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.txt2, textDecoration: "line-through" }}>{item.naam}</div>
            {item.notitie && <div style={{ fontSize: 11, color: C.txt3, marginTop: 2 }}>{item.notitie}</div>}
          </div>
          <button onClick={() => herstel(item.id)} style={{ ...btn(C.greenBg, C.green, C.greenBd), fontSize: 11 }}>Herstellen</button>
          <button onClick={() => verwijder(item.id)} style={{ ...btn(C.redBg, C.red, C.redBd), padding: "7px 10px" }}><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ITEM POPUP
// ─────────────────────────────────────────────────────────────────────────────
function ItemPopup({ mode, item, state, activeAfdId, onSave, onDelete, onArchive, onClose, onOpenRecept }) {
  const leeg = {
    id: uid(), naam: "", afdeling_id: activeAfdId, categorie_id: "",
    notitie: "", bestelartikelen: [], recept_id: null, checks: {}, archived: false,
  };
  const [form, setForm] = useState(item ? { ...item } : leeg);
  const [nieuwBa, setNieuwBa] = useState({ naam: "", hoeveelheid: "", eenheid: "kg", leverancier: "" });
  const [baFout, setBaFout] = useState(false);
  const naamRef = useRef();

  // Laad recepten uit ReceptenModule localStorage
  const recepten = (() => {
    try {
      const s = localStorage.getItem("kc-recepten-v1");
      if (s) return JSON.parse(s)?.recepten?.filter(r => !r.archived) || [];
    } catch {}
    return [];
  })();

  useEffect(() => { naamRef.current?.focus(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const gekoppeldRecept = recepten.find(r => r.id === form.recept_id);

  const addBa = () => {
    if (!nieuwBa.naam.trim()) { setBaFout(true); return; }
    setForm(f => ({ ...f, bestelartikelen: [...(f.bestelartikelen || []), { id: uid(), ...nieuwBa, naam: nieuwBa.naam.trim() }] }));
    setNieuwBa({ naam: "", hoeveelheid: "", eenheid: "kg", leverancier: "" });
    setBaFout(false);
  };
  const delBa = (id) => setForm(f => ({ ...f, bestelartikelen: f.bestelartikelen.filter(b => b.id !== id) }));

  const opslaan = () => {
    if (!form.naam.trim()) return naamRef.current?.focus();
    onSave(form);
  };

  const catOptions = state.categorieen.filter(c => c.afdeling_id === (form.afdeling_id || activeAfdId));

  return (
    <Popup onClose={onClose} title={mode === "new" ? "Nieuw item" : "Item bewerken"} breedte={540}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Naam */}
        <Veld label="Naam *">
          <input ref={naamRef} style={inp} placeholder="Naam van het item" value={form.naam} onChange={e => set("naam", e.target.value)} onKeyDown={e => e.key === "Enter" && opslaan()} />
        </Veld>

        {/* Afdeling + Categorie */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Veld label="Afdeling">
            <select style={inp} value={form.afdeling_id || ""} onChange={e => set("afdeling_id", e.target.value)}>
              <option value="">Kies…</option>
              {state.afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
            </select>
          </Veld>
          <Veld label="Categorie">
            <select style={inp} value={form.categorie_id || ""} onChange={e => set("categorie_id", e.target.value)}>
              <option value="">Geen</option>
              {catOptions.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
            </select>
          </Veld>
        </div>

        {/* Notitie */}
        <Veld label="Notitie">
          <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} placeholder="Optionele notitie…" value={form.notitie || ""} onChange={e => set("notitie", e.target.value)} />
        </Veld>

        {/* Recept koppeling */}
        <Veld label="Recept">
          {recepten.length === 0 ? (
            <div style={{ ...inp, color: C.txt3, fontSize: 12, display: "flex", alignItems: "center" }}>
              Nog geen recepten — voeg ze toe in Receptenbeheer
            </div>
          ) : (
            <div>
              <select style={{ ...inp, color: form.recept_id ? C.txt : C.txt3 }}
                value={form.recept_id || ""}
                onChange={e => set("recept_id", e.target.value || null)}>
                <option value="">— Geen recept gekoppeld —</option>
                {recepten.sort((a,b) => a.naam.localeCompare(b.naam,"nl")).map(r => (
                  <option key={r.id} value={r.id}>{r.naam}</option>
                ))}
              </select>
              {gekoppeldRecept && (
                <div style={{ marginTop: 6, padding: "6px 10px", background: C.priBg, border: `1px solid ${C.priBd}`, borderRadius: 6, fontSize: 11, color: C.pri, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ flex: 1 }}>
                    📖 {gekoppeldRecept.naam}
                    {gekoppeldRecept.allergenen?.length > 0 && (
                      <span style={{ color: C.red, marginLeft: 8 }}>⚠ {gekoppeldRecept.allergenen.filter(a => !["Tarwe","Rogge","Gerst","Haver","Amandelen","Hazelnoten","Walnoten","Cashewnoten","Pecannoten","Paranoten","Pistachenoten","Macadamianoten"].includes(a)).join(", ")}</span>
                    )}
                  </span>
                  {onOpenRecept && (
                    <button onClick={() => { onClose(); onOpenRecept(gekoppeldRecept.id); }}
                      style={{ ...btn(C.pri, "#fff", C.pri), padding: "4px 10px", fontSize: 11 }}>
                      Bekijk recept →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </Veld>

        {/* Bestelartikelen */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Bestelartikelen
          </div>
          {(form.bestelartikelen || []).length === 0 && (
            <div style={{ fontSize: 12, color: C.txt3, fontStyle: "italic", marginBottom: 8 }}>Nog geen bestelartikelen.</div>
          )}
          {(form.bestelartikelen || []).map(ba => (
            <div key={ba.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: C.bg3, borderRadius: 6, marginBottom: 4 }}>
              <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{ba.naam}</span>
              {ba.hoeveelheid && <span style={{ fontSize: 12, color: C.txt2 }}>{ba.hoeveelheid} {ba.eenheid}</span>}
              {ba.leverancier && <span style={{ fontSize: 11, color: C.txt3 }}>{ba.leverancier}</span>}
              <button onClick={() => delBa(ba.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 2, display: "flex" }}><Trash2 size={13} /></button>
            </div>
          ))}

          {/* Nieuw bestelartikel invoer */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 100px auto", gap: 6, marginTop: 8, alignItems: "start" }}>
            <input
              style={{ ...inp, ...(baFout ? { borderColor: C.red } : {}) }}
              placeholder="Artikel naam *"
              value={nieuwBa.naam}
              onChange={e => { setNieuwBa(f => ({ ...f, naam: e.target.value })); setBaFout(false); }}
              onKeyDown={e => e.key === "Enter" && addBa()}
            />
            <input style={inp} placeholder="Hoev." type="number" step="0.1" value={nieuwBa.hoeveelheid} onChange={e => setNieuwBa(f => ({ ...f, hoeveelheid: e.target.value }))} />
            <select style={inp} value={nieuwBa.eenheid} onChange={e => setNieuwBa(f => ({ ...f, eenheid: e.target.value }))}>
              {UNITS.map(u => <option key={u}>{u}</option>)}
            </select>
            <input style={inp} placeholder="Leverancier" value={nieuwBa.leverancier} onChange={e => setNieuwBa(f => ({ ...f, leverancier: e.target.value }))} />
            <button onClick={addBa} style={{ ...btn(C.pri, "#fff", C.pri), padding: "7px 10px", display: "flex", alignItems: "center" }}><Plus size={15} /></button>
          </div>
        </div>

        {/* Knoppen */}
        <div style={{ display: "flex", gap: 8, marginTop: 4, paddingTop: 14, borderTop: `1px solid ${C.bdr}` }}>
          <button onClick={opslaan} style={{ ...btn(C.pri, "#fff", C.pri), flex: 1 }}>
            💾 Opslaan
          </button>
          {mode === "edit" && (
            <>
              <button onClick={() => onArchive(form.id)} style={btn(C.bg3, C.txt2, C.bdr)}>
                <Archive size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Archief
              </button>
              <button onClick={() => { if (confirm("Item verwijderen?")) onDelete(form.id); }} style={btn(C.redBg, C.red, C.redBd)}>
                <Trash2 size={13} />
              </button>
            </>
          )}
        </div>
      </div>
    </Popup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AFDELING POPUP
// ─────────────────────────────────────────────────────────────────────────────
function AfdPopup({ afdelingen, onSave, onRename, onDelete, onClose }) {
  const [nieuw, setNieuw] = useState("");
  const [edit, setEdit] = useState(null); // { id, naam }

  const toevoegen = () => {
    if (!nieuw.trim()) return;
    onSave({ id: uid(), naam: nieuw.trim() });
    setNieuw("");
  };
  const opslaan = () => {
    if (!edit?.naam.trim()) return;
    onRename(edit.id, edit.naam.trim());
    setEdit(null);
  };

  return (
    <Popup onClose={onClose} title="Afdelingen beheren" breedte={400}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {afdelingen.map(a => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.id === a.id ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && opslaan()} />
                <button onClick={opslaan} style={btn(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btn(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{a.naam}</span>
                <button onClick={() => setEdit({ ...a })} style={btn(C.bg3, C.txt2, C.bdr)}>✏</button>
                <button onClick={() => { if (confirm(`Afdeling "${a.naam}" en alle bijbehorende categorieën verwijderen?`)) onDelete(a.id); }} style={btn(C.redBg, C.red, C.redBd)}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 8, paddingTop: 12, borderTop: `1px solid ${C.bdr}` }}>
          <input style={{ ...inp, flex: 1 }} placeholder="Nieuwe afdeling…" value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && toevoegen()} />
          <button onClick={toevoegen} style={btn(C.pri, "#fff", C.pri)}>+ Toevoegen</button>
        </div>
      </div>
    </Popup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIE POPUP
// ─────────────────────────────────────────────────────────────────────────────
function CatPopup({ categorieen, afdelingen, activeAfdId, onSave, onRename, onDelete, onClose }) {
  const [nieuw, setNieuw] = useState("");
  const [selAfd, setSelAfd] = useState(activeAfdId || afdelingen[0]?.id || "");
  const [edit, setEdit] = useState(null);

  const gefilterd = categorieen.filter(c => c.afdeling_id === selAfd);

  const toevoegen = () => {
    if (!nieuw.trim() || !selAfd) return;
    onSave({ id: uid(), naam: nieuw.trim(), afdeling_id: selAfd });
    setNieuw("");
  };
  const opslaan = () => {
    if (!edit?.naam.trim()) return;
    onRename(edit.id, edit.naam.trim());
    setEdit(null);
  };

  return (
    <Popup onClose={onClose} title="Categorieën beheren" breedte={440}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {afdelingen.length > 1 && (
          <Veld label="Afdeling">
            <select style={inp} value={selAfd} onChange={e => setSelAfd(e.target.value)}>
              {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
            </select>
          </Veld>
        )}
        {gefilterd.map(c => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.id === c.id ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && opslaan()} />
                <button onClick={opslaan} style={btn(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btn(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{c.naam}</span>
                <button onClick={() => setEdit({ ...c })} style={btn(C.bg3, C.txt2, C.bdr)}>✏</button>
                <button onClick={() => { if (confirm(`Categorie "${c.naam}" verwijderen? Items verhuizen naar "Geen categorie".`)) onDelete(c.id); }} style={btn(C.redBg, C.red, C.redBd)}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
        {gefilterd.length === 0 && <p style={{ fontSize: 12, color: C.txt3, fontStyle: "italic" }}>Nog geen categorieën in deze afdeling.</p>}
        <div style={{ display: "flex", gap: 8, marginTop: 4, paddingTop: 12, borderTop: `1px solid ${C.bdr}` }}>
          <input style={{ ...inp, flex: 1 }} placeholder="Nieuwe categorie…" value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && toevoegen()} />
          <button onClick={toevoegen} style={btn(C.pri, "#fff", C.pri)}>+ Toevoegen</button>
        </div>
      </div>
    </Popup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GEDEELDE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Popup wrapper
function Popup({ children, onClose, title, breedte = 480 }) {
  // Sluit op Escape
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg2, borderRadius: 12, width: "100%", maxWidth: breedte, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "16px 20px", borderBottom: `1px solid ${C.bdr}` }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: C.txt, flex: 1 }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, display: "flex", padding: 4 }}><X size={18} /></button>
        </div>
        {/* Body */}
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Veld({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}
